import React, { useEffect, useState } from 'react';
import AdminEmsShipping from '../../components/admin/AdminEmsShipping';
import AdminPickupShipping from '../../components/admin/AdminPickupShipping';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { Loader2, Save, Truck, Webhook } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CARRIER_OPTIONS = [
  { id: 'tcc', label: 'TCC' },
  { id: 'fedex', label: 'FedEx' },
  { id: 'dhl', label: 'DHL' },
  { id: 'coordinadora', label: 'Coordinadora' },
  { id: 'servientrega', label: 'Servientrega' },
  { id: 'interrapidisimo', label: 'Interrapidísimo' },
  { id: 'envia', label: 'Envía Domestic' },
];

/**
 * Envía shipping configuration screen — /admin/settings/shipping
 * Persists to /api/shipping/config/envia via admin JWT.
 */
const AdminShipping = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [cfg, setCfg] = useState({
    api_key_masked: '',
    has_key: false,
    mode: 'sandbox',
    origin: {},
    default_package: {},
    carriers: [],
    auto_create_shipment: true,
    webhook_registered: false,
    webhook_id: null,
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const r = await axios.get(`${API_URL}/api/shipping/config/envia`, { headers: { Authorization: `Bearer ${token}` } });
      setCfg(r.data);
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'Could not load Envía config', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const body = {
        mode: cfg.mode,
        origin: cfg.origin,
        default_package: cfg.default_package,
        carriers: cfg.carriers,
        auto_create_shipment: cfg.auto_create_shipment,
        auto_pickup: cfg.auto_pickup,
        insure_shipments: cfg.insure_shipments,
        pickup_instructions: cfg.pickup_instructions || '',
        pickup_saturday: cfg.pickup_saturday,
      };
      if (newApiKey.trim()) body.api_key = newApiKey.trim();
      await axios.put(`${API_URL}/api/shipping/config/envia`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Saved', description: 'Envía configuration updated' });
      setNewApiKey('');
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const registerWebhook = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const r = await axios.post(`${API_URL}/api/shipping/config/envia/register-webhook`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.registered) {
        toast({ title: 'Webhook registered', description: `Callback: ${r.data.callback_url}` });
      } else {
        toast({ title: 'Webhook not registered', description: 'Check Envía dashboard manually', variant: 'destructive' });
      }
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Registration failed', variant: 'destructive' });
    }
  };

  const setOrigin = (patch) => setCfg({ ...cfg, origin: { ...cfg.origin, ...patch } });
  const setPkg = (patch) => setCfg({ ...cfg, default_package: { ...cfg.default_package, ...patch } });
  const toggleCarrier = (id, on) =>
    setCfg({ ...cfg, carriers: on ? [...(cfg.carriers || []), id] : (cfg.carriers || []).filter((c) => c !== id) });

  const onAddressAutocomplete = (addr) => {
    setOrigin({
      street: addr.street || cfg.origin.street,
      number: addr.number || cfg.origin.number,
      city: addr.city || cfg.origin.city,
      state: addr.state || cfg.origin.state,   // ISO 3166-2 short code from Google
      postal_code: addr.postalCode || cfg.origin.postal_code,
      country: (addr.country || cfg.origin.country || 'CO').toUpperCase(),
    });
    toast({ title: 'Address filled from Google Places' });
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="admin-shipping-page">
      <div className="flex items-center gap-3">
        <Truck className="w-6 h-6 text-[#be1f1a]" />
        <h1 className="text-2xl font-serif text-[#2C2420]">Envía Shipping</h1>
      </div>

      {/* API key + mode */}
      <Card>
        <CardHeader>
          <CardTitle>API Credentials</CardTitle>
          <CardDescription>Configure Envía.com API key and toggle between sandbox/production.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label>API Key</Label>
            <div className="flex items-center gap-3">
              <Input
                data-testid="envia-api-key-input"
                type="password"
                placeholder={cfg.has_key ? cfg.api_key_masked : 'Paste API key…'}
                value={newApiKey}
                onChange={(e) => setNewApiKey(e.target.value)}
              />
              {cfg.has_key && <span className="text-xs text-green-700 whitespace-nowrap">✓ key stored</span>}
            </div>
            <p className="text-xs text-gray-500">Leave empty to keep the current key. Anything you type here replaces it.</p>
          </div>
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <Label>Mode</Label>
              <p className="text-sm text-gray-500">
                <b>{cfg.mode === 'production' ? 'Producción' : 'Sandbox'}</b> — {cfg.mode === 'production' ? 'genera guías reales, cobra el envío' : 'llamadas de prueba, no genera guías reales'}
              </p>
            </div>
            <Switch
              data-testid="envia-mode-toggle"
              checked={cfg.mode === 'production'}
              onCheckedChange={(on) => setCfg({ ...cfg, mode: on ? 'production' : 'sandbox' })}
            />
          </div>
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <Label>Auto-create shipment after payment</Label>
              <p className="text-sm text-gray-500">Generate the label / tracking number automatically when Stripe confirms payment.</p>
            </div>
            <Switch
              data-testid="envia-auto-create-toggle"
              checked={!!cfg.auto_create_shipment}
              onCheckedChange={(on) => setCfg({ ...cfg, auto_create_shipment: on })}
            />
          </div>
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <Label>Insure packages for the products' value</Label>
              <p className="text-sm text-gray-500">
                Declares and insures every package for the cart's product subtotal (domestic carrier insurance, or
                Envía insurance for international). The cost is part of the shipping price the customer pays.
                Off: carriers apply only their minimum coverage.
              </p>
            </div>
            <Switch
              data-testid="envia-insure-toggle"
              checked={cfg.insure_shipments !== false}
              onCheckedChange={(on) => setCfg({ ...cfg, insure_shipments: on })}
            />
          </div>
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <Label>Schedule carrier pickup automatically</Label>
              <p className="text-sm text-gray-500">
                After each label, book the carrier's first pickup window at the origin address (same day before the
                carrier's cutoff, otherwise the next business day). If it can't be booked, the order shows the nearest
                drop-off branches instead. Pickups may carry a fee in Envía.
              </p>
            </div>
            <Switch
              data-testid="envia-auto-pickup-toggle"
              checked={cfg.auto_pickup !== false}
              onCheckedChange={(on) => setCfg({ ...cfg, auto_pickup: on })}
            />
          </div>
          <div className="flex items-center justify-between border rounded-md p-3">
            <div>
              <Label>Saturday pickups</Label>
              <p className="text-sm text-gray-500">Off: pickups Monday to Friday only.</p>
            </div>
            <Switch
              data-testid="envia-pickup-saturday-toggle"
              checked={!!cfg.pickup_saturday}
              onCheckedChange={(on) => setCfg({ ...cfg, pickup_saturday: on })}
            />
          </div>
          {cfg.webhook_url && (
            <div className="grid gap-2">
              <Label>Tracking webhook URL (paste it in Envía → Webhooks, type onShipmentStatusUpdate)</Label>
              <Input readOnly value={cfg.webhook_url} onFocus={(e) => e.target.select()} data-testid="envia-webhook-url" />
              <p className="text-xs text-gray-500">Contains a secret token: updates sent without it are rejected.</p>
            </div>
          )}
          <div className="grid gap-2">
            <Label>Pickup instructions for the driver</Label>
            <Input
              data-testid="envia-pickup-instructions"
              maxLength={200}
              placeholder="e.g. Ask for Esperanza at the front desk"
              value={cfg.pickup_instructions || ''}
              onChange={(e) => setCfg({ ...cfg, pickup_instructions: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Origin address */}
      <Card>
        <CardHeader>
          <CardTitle>Sender / Origin Address</CardTitle>
          <CardDescription>
            The warehouse the package ships from. Use Google autocomplete or fill in manually.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label>Autocompletar con Google</Label>
            <AddressAutocomplete onAddressSelect={onAddressAutocomplete} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input value={cfg.origin.name || ''} onChange={(e) => setOrigin({ name: e.target.value })} /></div>
            <div><Label>Company</Label><Input value={cfg.origin.company || ''} onChange={(e) => setOrigin({ company: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={cfg.origin.email || ''} onChange={(e) => setOrigin({ email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={cfg.origin.phone || ''} onChange={(e) => setOrigin({ phone: e.target.value })} /></div>
            <div><Label>Tax ID (EIN / NIT) — required on international labels</Label><Input value={cfg.origin.identification_number || ''} onChange={(e) => setOrigin({ identification_number: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Street</Label><Input value={cfg.origin.street || ''} onChange={(e) => setOrigin({ street: e.target.value })} /></div>
            <div><Label>Number</Label><Input value={cfg.origin.number || ''} onChange={(e) => setOrigin({ number: e.target.value })} /></div>
            <div><Label>District</Label><Input value={cfg.origin.district || ''} onChange={(e) => setOrigin({ district: e.target.value })} /></div>
            <div><Label>City</Label><Input value={cfg.origin.city || ''} onChange={(e) => setOrigin({ city: e.target.value })} /></div>
            <div>
              <Label>State (ISO code, ≤3 chars — e.g. BOG, ANT)</Label>
              <Input value={cfg.origin.state || ''} maxLength={3} onChange={(e) => setOrigin({ state: e.target.value.toUpperCase() })} />
            </div>
            <div><Label>Postal Code</Label><Input value={cfg.origin.postal_code || ''} onChange={(e) => setOrigin({ postal_code: e.target.value })} /></div>
            <div><Label>Country (ISO)</Label><Input value={cfg.origin.country || ''} maxLength={2} onChange={(e) => setOrigin({ country: e.target.value.toUpperCase() })} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Default package */}
      <Card>
        <CardHeader>
          <CardTitle>Default Package Dimensions</CardTitle>
          <CardDescription>Used when quoting rates and generating labels. Override per-product later if needed.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label>Length (cm)</Label><Input type="number" value={cfg.default_package.length ?? ''} onChange={(e) => setPkg({ length: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Width (cm)</Label><Input type="number" value={cfg.default_package.width ?? ''} onChange={(e) => setPkg({ width: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Height (cm)</Label><Input type="number" value={cfg.default_package.height ?? ''} onChange={(e) => setPkg({ height: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Weight (kg)</Label><Input type="number" step="0.1" value={cfg.default_package.weight ?? ''} onChange={(e) => setPkg({ weight: parseFloat(e.target.value) || 0 })} /></div>
            <div className="col-span-2"><Label>Contenido</Label><Input value={cfg.default_package.content || ''} onChange={(e) => setPkg({ content: e.target.value })} /></div>
            <div className="col-span-2"><Label>Declared value per item</Label><Input type="number" value={cfg.default_package.amount ?? ''} onChange={(e) => setPkg({ amount: parseFloat(e.target.value) || 0 })} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Carriers */}
      <Card>
        <CardHeader>
          <CardTitle>Preferred Carriers</CardTitle>
          <CardDescription>Only these carriers will be queried at checkout. Uncheck the ones you don&apos;t use.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {CARRIER_OPTIONS.map((c) => (
              <label key={c.id} className="flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  checked={(cfg.carriers || []).includes(c.id)}
                  onCheckedChange={(v) => toggleCarrier(c.id, !!v)}
                  data-testid={`envia-carrier-${c.id}`}
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Webhook className="w-4 h-4" /> Webhooks</CardTitle>
          <CardDescription>
            Register Envía to POST tracking updates to <code>/api/shipping/webhooks/envia</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {cfg.webhook_registered ? (
            <div className="border-l-4 border-green-500 bg-green-50 p-3 rounded text-sm text-green-900">
              ✓ Webhook is registered (id: <code>{cfg.webhook_id}</code>)
            </div>
          ) : (
            <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded text-sm text-amber-900">
              Webhook not yet registered. Tracking updates won&apos;t sync automatically until you register it.
            </div>
          )}
          <Button variant="outline" onClick={registerWebhook} data-testid="envia-register-webhook-btn">
            <Webhook className="w-4 h-4 mr-2" />
            {cfg.webhook_registered ? 'Re-register webhook' : 'Register webhook'}
          </Button>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end gap-2 pb-8">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#be1f1a] hover:bg-[#a71b17]"
          data-testid="envia-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save configuration
        </Button>
      </div>

      <AdminEmsShipping />

      <AdminPickupShipping />
    </div>
  );
};

export default AdminShipping;
